// QUE SOBREVIVE A UN PEGADO EN EL EDITOR CON FORMATO.
//
// ── EL PROBLEMA ────────────────────────────────────────────────────────────
//
// Crismar Perez (Maricarmen Nieto), 2026-09-17: creo una carta nueva para los
// prospectos, la vista previa se veia bien, y al enviarla salia
//
//   Invalid input - Body text: String must contain at most 20000 character(s)
//
// El editor de plantillas de Firmas es desde el 2026-09-15 el de los correos
// (#2095), o sea un `contentEditable`, y lo que guarda es `innerHTML` TAL CUAL.
// Cuando alguien pega una carta desde Word o Google Docs, el navegador mete el
// HTML de origen entero: `mso-` por todas partes, `<o:p>`, `class="MsoNormal"`,
// `lang=`, `<span style="font-family:'Calibri',sans-serif;font-size:11.0pt">`
// alrededor de cada palabra. Una carta de UNA pagina se convierte en cientos de
// kilobytes. El texto es el mismo; lo que pesa es la basura.
//
// ── LA REGLA: SE QUEDA LO QUE ESTE EDITOR SABE HONRAR ──────────────────────
//
// No es una lista de "lo peligroso" —de eso ya se encarga `sanitizeHtml.js` al
// PINTAR— sino de lo que este editor puede producir con sus propios botones y,
// en Firmas, de lo que el PDF firmado es capaz de dibujar.
//
// Esa segunda mitad es la que importa. El documento que el cliente firma no se
// enseña en un navegador y ya: se DIBUJA con pdf-lib, linea a linea, y quien lo
// lee es `deskte-api/src/utils/documentoConFormato.ts`, que entiende los
// bloques, `<b> <strong> <i> <em> <u>`, `<br>` y el `text-align` — y se salta
// todo lo demas. Guardar un `font-family` que el PDF no dibuja no es inofensivo:
// es la pantalla del agente y el documento firmado enseñando cosas distintas,
// que es justo lo que el #2095 se cuido de evitar quitando los botones de
// imagen, enlace y tamaño de letra en ese editor.
//
// Por eso las opciones de aqui son LAS MISMAS BANDERAS del editor
// (`sinImagen` / `sinEnlace` / `sinFuente`): lo que se pega puede traer
// exactamente lo que la barra de esa pantalla puede hacer, ni mas ni menos.
//
// ── POR QUE ESTE ARCHIVO NO IMPORTA NADA ───────────────────────────────────
//
// Porque la decision se prueba desde las pruebas del backend, que corren en
// Node y no instalan las dependencias de `crm-app`. El recorrido del DOM —que
// si necesita DOMPurify y un navegador— vive en `sanitizeHtml.js`, al lado del
// otro limpiador. Aqui solo esta el criterio, que es lo que se rompe en
// silencio si alguien lo cambia.

/**
 * Los bloques y las marcas que el editor produce y el PDF sabe dibujar.
 *
 * `span` entra porque es donde `document.execCommand` con `styleWithCSS` deja
 * la negrita y la cursiva; se queda solo si le sobrevive algun estilo util (ver
 * `estiloPermitido`), y si no, `limpiarHtmlPegado` lo deshace.
 */
export const ETIQUETAS_BASE = [
  'p', 'div', 'br', 'span',
  'b', 'strong', 'i', 'em', 'u',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'ul', 'ol', 'li', 'blockquote',
];

/**
 * Las propiedades de `style` que se conservan SIEMPRE.
 *
 * Son las cuatro que el dibujante del PDF lee (`alineacionDe` y
 * `marcasDelStyle` en documentoConFormato.ts). `text-decoration-line` va
 * aparte porque algunos navegadores parten `text-decoration` en sus piezas.
 */
export const ESTILOS_BASE = ['text-align', 'font-weight', 'font-style', 'text-decoration', 'text-decoration-line'];

/** Las que solo valen donde la barra ofrece letra y tamaño: los correos. */
export const ESTILOS_DE_FUENTE = ['font-family', 'font-size'];

/** Cuanto puede ocupar el `style` de UNA etiqueta ya limpio. */
const TOPE_DE_ESTILO = 200;

/** Las etiquetas que se dejan pasar, segun lo que la barra de esa pantalla ofrezca. */
export function etiquetasPermitidas(opciones = {}) {
  const fuera = [];
  if (!opciones.sinEnlace) fuera.push('a');
  if (!opciones.sinImagen) fuera.push('img');
  // `font` no lo escribe nadie a mano, pero `execCommand` todavia lo emite en
  // algunos navegadores cuando `styleWithCSS` no prende.
  if (!opciones.sinFuente) fuera.push('font');
  return [...ETIQUETAS_BASE, ...fuera];
}

/** Los atributos que se dejan pasar. */
export function atributosPermitidos(opciones = {}) {
  const out = ['style', 'align'];
  if (!opciones.sinEnlace) out.push('href', 'target', 'rel');
  if (!opciones.sinImagen) out.push('src', 'alt', 'width', 'height');
  if (!opciones.sinFuente) out.push('face', 'size', 'color');
  return out;
}

/**
 * El `style` de una etiqueta, recortado a lo que se honra.
 *
 * Devuelve cadena vacia cuando no queda nada — quien llama entonces quita el
 * atributo entero en vez de dejar un `style=""`.
 *
 * SE RECHAZA CUALQUIER VALOR CON PARENTESIS. `url(...)` y `expression(...)` son
 * las dos formas de meter algo que no es un estilo dentro de uno, y ninguna
 * propiedad de esta lista los necesita: una alineacion, un grosor o un nombre
 * de letra no llevan parentesis nunca. Es una linea y cierra las dos.
 */
export function estiloPermitido(valor, opciones = {}) {
  const permitidas = new Set(opciones.sinFuente ? ESTILOS_BASE : [...ESTILOS_BASE, ...ESTILOS_DE_FUENTE]);
  const piezas = [];
  for (const trozo of String(valor ?? '').split(';')) {
    const corte = trozo.indexOf(':');
    if (corte < 0) continue;
    const prop = trozo.slice(0, corte).trim().toLowerCase();
    const val = trozo.slice(corte + 1).trim();
    if (!prop || !val) continue;
    if (!permitidas.has(prop)) continue;
    if (val.includes('(') || val.includes(')')) continue;
    piezas.push(`${prop}: ${val}`);
  }
  const out = piezas.join('; ');
  // Un `style` que se pasa de largo es basura de Word por definicion: ninguna
  // de estas cinco propiedades junta 200 caracteres escribiendola a mano.
  return out.length > TOPE_DE_ESTILO ? '' : out;
}
